<script setup lang="ts">
  import { ref } from 'vue'
  import { onClickOutside } from '@vueuse/core'
  import { useFlipbooksMenuStore } from '@/stores/list-item-menu'
  import { useFlipbooksStore } from '@/stores/flipbooks'
  import { usePopupStore } from '@/stores/popup'
  import { useLoaderStore } from '@/stores/loader'
  import { useModalStore } from '@/stores/modal'
  import { useBrowserStore } from '@/stores/browser'
  import { useUserStore } from '@/stores/user'
  import DesMenuOptions from '@/components/menu/DesMenuOptions.vue'
  import DesMenuItem from '@/components/menu/DesMenuItem.vue'
  import DesMenuSeparator from '@/components/menu/DesMenuSeparator.vue'
  import type { TFlipbookProjectOptions } from '@/types/flipbooks'
  import type { TUser } from '@/types/user'
  import type { TAccountType } from '@/types/user'

  const menu = useFlipbooksMenuStore()
  const flipbooks = useFlipbooksStore()
  const popup = usePopupStore()
  const loader = useLoaderStore()
  const modal = useModalStore()
  const browser = useBrowserStore()
  const users = useUserStore()

  const accountType = ref<TAccountType>(users.accountType)

  const target = ref<HTMLElement | null>(null)
  onClickOutside(target, () => menu.item && menu.close())

  async function openPDF() {
    if (!menu.item) return
    await browser.openNewWindowTab(menu.item.pdf_url)
  }

  async function deleteProject() {
    if (!menu.item) return

    const result = await popup.confirm('Are you sure you want to delete this flipbook?', {
      content: 'This action is irreversible.',
      labelCancel: 'Cancel',
      labelConfirm: 'Delete',
    })

    if (result !== false) {
      await flipbooks.deleteFlipbook(menu.item)
    }
  }

  async function embedFlipbook() {
    if (!menu?.item?.id || isNaN(menu.item.id)) {
      return
    }

    loader.show('Getting data...')
    const flipbookUrl = await flipbooks.buildFlipbookUrl(menu.item.id, menu.item.token, 'FP')
    const createFlipbookData = null
    loader.hide()

    modal.open(() => import('@/components/modal/DesModalFlipbookEmbed.vue'), {
      createFlipbookData,
      flipbookUrl,
    })
  }

  async function fileInformation() {
    if (!menu?.item) {
      return
    }

    const authorUser: TUser | null = await users.getUserById(menu.item.user_id)

    modal.open(() => import('@/components/modal/DesModalFileInfo.vue'), {
      item: menu.item,
      authorUser,
    })
  }

  async function legacyCover() {
    await modal.open(() => import('@/components/modal/DesModalCovers.vue'), {
      id: menu.item?.id,
      pdfUrl: menu.item?.pdf_url,
    })
  }

  async function customizeFlipbook() {
    if (!menu?.item) {
      return
    }

    loader.show('Getting data...')

    const flipbookUrl = await flipbooks.buildFlipbookUrl(menu.item.id, menu.item.token, 'FP')
    let currentFlipbookOptions: TFlipbookProjectOptions | null = null
    try {
      currentFlipbookOptions = await flipbooks.getFlipbookProjectOptions(menu.item.id)
    } catch {
      // ignore
    }

    loader.hide()

    const flipbookProjectId: number = menu.item.id
    modal.open(() => import('@/components/modal/DesModalCustomizeFlipbook.vue'), {
      flipbookProjectId,
      flipbookUrl,
      currentFlipbookOptions,
    })
  }
</script>

<template>
  <div
    class="absolute z-50"
    :style="{ left: menu.position.left + 'px', top: menu.position.top + 'px' }"
    ref="target"
    @click="menu.close()"
  >
    <DesMenuOptions :open="menu.opened" :left="menu.flip" :right="!menu.flip">
      <DesMenuItem
        v-if="accountType['3d_covers']"
        @click="legacyCover()"
        icon="fa-light fa-cube"
        event="projects-flipbook-menu"
      >
        Cover & Mockup Creator
      </DesMenuItem>
      <DesMenuItem @click="openPDF()" icon="fa-light fa-file-pdf" event="projects-flipbook-menu">Open PDF</DesMenuItem>
      <DesMenuItem @click="embedFlipbook()" icon="fa-light fa-file-code" event="projects-flipbook-menu">
        Generate flipbook embed code
      </DesMenuItem>
      <DesMenuItem @click="customizeFlipbook()" icon="fa-light fa-file-code" event="projects-flipbook-menu">
        Customize flipbook
      </DesMenuItem>
      <DesMenuItem @click="fileInformation()" icon="fa-light fa-circle-info" event="projects-flipbook-menu">
        Project information
      </DesMenuItem>
      <DesMenuSeparator />
      <DesMenuItem @click="deleteProject()" icon="fa-light fa-trash-can" danger event="projects-flipbook-menu">
        Delete
      </DesMenuItem>
    </DesMenuOptions>
  </div>
</template>
